<template>
    <loading-element v-if="authStore.isLoading" />
    <div class="main bg-primary" v-else>
        <div class="rowSpaceBetween fullWidth mb-5">
            <TopDeck
                v-if="authStore.userInfos"
                firstElement="Catalogue"
                secondElement="d'ingrédients de base"
                :coloredSecond="true"
                actionHeaderIcon="mdi-arrow-left"
                :actionheaderFunction="() => $router.push('/my_ingredients')"
                actionHeaderColor="accent"
                actionHeaderTextColor="white"
            />
            <div class="rowCustom">
                <ThemeToggle />
                <AccountToggle />
            </div>
        </div>

        <div v-if="isAdmin">
            <v-btn
                color="accent"
                rounded="lg"
                @click="router.push('/base_ingredients')"
                class="white-text-button mb-3 mr-3"
            >
                <v-icon class="mr-1">mdi-plus</v-icon>
                <span>Ajouter des ingrédients de base</span>
            </v-btn>

            <v-btn
                color="accent"
                rounded="lg"
                @click="router.push('/hidden_base_ingredients')"
                class="white-text-button mb-3"
            >
                <v-icon class="mr-1">mdi-plus</v-icon>
                <span>Ajouter des ingrédients de base cachées</span>
            </v-btn>
        </div>

        <v-row no-gutters>
        <v-col cols="12" md="12" class="pr-2">
            <div class="pa-4 bg-secondary rounded-xl">
                <div class="d-flex align-center justify-space-between">
                    <h1 class="text">Ingrédients de base</h1>
                </div>

                <div
                    class="d-flex align-center my-4"
                    style="gap: 1rem; flex-wrap: wrap"
                >
                    <v-text-field
                        v-model="searchQuery"
                        placeholder="Rechercher (titre, catégorie, notes)"
                        hide-details
                        density="compact"
                        variant="tonal"
                        prepend-inner-icon="mdi-magnify"
                        class="searchField rounded-lg flex-1-1 bg-tertiary"
                        rounded="lg"
                    />

                    <v-select
                        v-model="selectedCategory"
                        :items="categories"
                        label="Catégorie"
                        hide-details
                        density="compact"
                        variant="tonal"
                        class="categorySelector rounded-lg"
                        style="min-width: 200px"
                    />
                </div>

                <!-- Loader -->
                <div v-if="isLoadingIngredients" class="d-flex justify-center my-10">
                    <v-progress-circular indeterminate color="primary" />
                </div>

                <!-- Vide -->
                <v-alert
                    v-else-if="!filteredIngredients.length"
                    type="info"
                    variant="tonal"
                    class="mt-4"
                >
                    Aucune recette à afficher.
                </v-alert>

                <!-- Tableau -->
                <v-table density="compact" class="bg-tertiary rounded-lg" v-if="filteredIngredients.length">
                    <thead>
                    <tr>
                        <th>Ingrédient</th>
                        <!-- icône + image -->
                        <th>Nom</th>
                        <th>Catégorie</th>
                        <th>Marque</th>
                        <th>Portion (g)</th>
                        <th>Ajouter</th>
                        <th v-if="isAdmin">Supprimer</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr
                        v-for="item in filteredIngredients"
                        :key="item.id"
                        class="ingredients"
                    >
                        <td class="d-flex align-center">
                        <v-img
                            v-if="item.productImage"
                            :src="item.productImage"
                            width="32"
                            height="32"
                            fit
                        />
                        <v-icon v-else color="grey lighten-1" size="32"
                            >mdi-food</v-icon
                        >
                        </td>
                        <td>{{ item.productName }}</td>
                        <td>{{ item.category }}</td>
                        <td>{{ item.productBrand ?? "Inconnu" }}</td>
                        <td>{{ item.servingQuantity }} g</td>
                        <td>
                            <v-btn @click.stop="addItemToUserCollection(item)" size="small" color="accent">
                                Ajouter
                            </v-btn>
                        </td>
                        <td v-if="isAdmin">
                            <v-btn
                                @click.stop="requestDelete(item)"
                                size="small"
                                color="error"
                                variant="tonal"
                            >
                                Supprimer
                            </v-btn>
                        </td>
                    </tr>
                    </tbody>
                </v-table>
            </div>
        </v-col>
        </v-row>

        <v-snackbar v-model="snack.show" :timeout="2500">
            {{ snack.text }}
        </v-snackbar>
        <v-snackbar v-if="errorMessage" color="error" timeout="3000" top right>
            {{ errorMessage }}
        </v-snackbar>
        <ConfirmDialog
            v-model="confirmDialogOpen"
            title="Confirmer la suppression"
            :message="confirmDialogMessage"
            confirm-text="Supprimer"
            cancel-text="Annuler"
            confirm-color="error"
            cancel-color="grey"
            confirm-variant="elevated"
            cancel-variant="text"
            :max-width="480"
            @confirm="confirmDeleteBaseIngredient"
            @cancel="cancelDeleteBaseIngredient"
        />
    </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { useRouter } from "vue-router";
import LoadingElement from "@/components/Animations/LoadingElement.vue";
import TopDeck from "@/components/Utils/TopDeck.vue";
import ConfirmDialog from "@/components/Utils/ConfirmDialog.vue";
import { useAuthStore } from "@/store/auth_store";
import ThemeToggle from "@/components/Utils/ThemeToggle.vue";
import { addItemToUserCollection as addItemToUserCollectionService } from '@/service/ingredient_service'
import { getBaseIngredients, deleteBaseIngredient } from '@/service/base_ingredient_service'
import AccountToggle from "@/components/Utils/AccountToggle.vue";

export default {
    name: "BaseIngredientsCatalog",
    components: { LoadingElement, TopDeck, ThemeToggle, AccountToggle, ConfirmDialog },
    setup() {
        const authStore = useAuthStore();
        const errorMessage = ref("");
        const snack = ref({ show: false, text: "" });
        const router = useRouter();

        const ingredients = ref([]);
        const isLoadingIngredients = ref(false);
        const searchQuery = ref("");
        const selectedCategory = ref("Toutes");
        const isAdmin = computed(() => authStore.userInfos?.role === 'admin');
        const confirmDialogOpen = ref(false);
        const ingredientToDelete = ref(null);
        const confirmDialogMessage = computed(() => {
            const name = ingredientToDelete.value?.productName || ingredientToDelete.value?.id || "";
            return name
                ? `Supprimer l'ingrédient de base "${name}" ?`
                : "Supprimer cet ingrédient de base ?";
        });

        const categories = computed(() => {
            const cats = new Set(
                ingredients.value.map((i) => i.category).filter(Boolean)
            );
            return ["Toutes", ...Array.from(cats).sort()];
        });

        async function addItemToUserCollection(item) {
            if (!authStore?.userid) {
                errorMessage.value = "Utilisateur non connecté.";
                return;
            }

            try {
                const res = await addItemToUserCollectionService(authStore.userid, item, item.id);
                if (res?.existed) {
                    snack.value = { show: true, text: "Déjà dans votre collection." };
                } else {
                    snack.value = { show: true, text: "Ajouté à votre collection." };
                }
            } catch (e) {
                console.error('Erreur ajout ingrédient utilisateur:', e);
                errorMessage.value = "Erreur lors de l'ajout de l'ingrédient.";
            }
        }

        async function deleteBaseIngredientItem(item) {
            if (!item?.id) return;
            if (!isAdmin.value) {
                errorMessage.value = "Action réservée aux administrateurs.";
                return;
            }

            try {
                await deleteBaseIngredient(item.id, authStore.userInfos?.role);
                ingredients.value = ingredients.value.filter((ing) => ing.id !== item.id);
                snack.value = { show: true, text: "Ingrédient supprimé." };
            } catch (e) {
                console.error("Erreur suppression ingrédient de base:", e);
                errorMessage.value = "Erreur lors de la suppression de l'ingrédient.";
            }
        }

        function requestDelete(item) {
            if (!item) return;
            if (!isAdmin.value) {
                errorMessage.value = "Action réservée aux administrateurs.";
                return;
            }
            ingredientToDelete.value = item;
            confirmDialogOpen.value = true;
        }

        function cancelDeleteBaseIngredient() {
            confirmDialogOpen.value = false;
            ingredientToDelete.value = null;
        }

        async function confirmDeleteBaseIngredient() {
            if (!ingredientToDelete.value) return;
            const item = ingredientToDelete.value;
            ingredientToDelete.value = null;
            confirmDialogOpen.value = false;
            await deleteBaseIngredientItem(item);
        }

        const filteredIngredients = computed(() => {
            if (!ingredients.value) return [];

            let result = ingredients.value;

            if (selectedCategory.value && selectedCategory.value !== "Toutes") {
                result = result.filter(item => item.category === selectedCategory.value);
            }

            if (searchQuery.value.trim()) {
                const q = searchQuery.value.toLowerCase();
                result = result.filter(item =>
                    item.productName?.toLowerCase().includes(q) ||
                    item.category?.toLowerCase().includes(q) ||
                    item.productBrand?.toLowerCase().includes(q)
                );
            }

            return result;
        });

        onMounted(() => {
            watch(
                () => authStore.userInfos,
                async () => {
                    if (!authStore?.userid) return;
                    try {
                        isLoadingIngredients.value = true;
                        const data = await getBaseIngredients();
                        ingredients.value = Object.entries(data).map(([id, value]) => ({
                            id,
                            ...value,
                        }));
                        
                    } catch (error) {
                        console.error("Erreur récupération recettes:", error);
                        errorMessage.value = "Erreur lors du chargement des recettes.";
                    } finally {
                        isLoadingIngredients.value = false;
                    }
                },
                { immediate: true }
            );
        });

        return {
            authStore,
            errorMessage,
            snack,
            router,
            searchQuery,
            selectedCategory,
            categories,
            addItemToUserCollection,
            requestDelete,
            confirmDialogOpen,
            confirmDialogMessage,
            confirmDeleteBaseIngredient,
            cancelDeleteBaseIngredient,
            isLoadingIngredients,
            filteredIngredients,
            isAdmin,
        };
    },
};
</script>

<style scoped>

    .searchField {
        background-color: rgb(244, 244, 244);
        width: 70%;
    }

    .recipe-card {
        border-radius: 12px;
    }

    .cursor-pointer {
        cursor: pointer;
    }

    .recipeContainerTitle {
        transition: color 0.2s;
    }

    .line-clamp-1 {
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .rowCustom {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
    }
</style>
